/**
 * Presentation helpers for catalog metadata on the public music UI.
 * Keep admin/system fields out of the listening experience.
 */

export const LISTENABLE_TYPES = ["song", "album", "playlist"];

/** Fallback hype line when an upcoming item has no custom preReleaseMessage. */
export const DEFAULT_PRE_RELEASE_MESSAGE =
  "Something new from Ignite Elysium is almost here… get ready for this one.";

/** Public-facing pre-release copy (custom, else default). */
export const getEffectivePreReleaseMessage = (itemOrMessage) => {
  const raw =
    itemOrMessage && typeof itemOrMessage === "object"
      ? itemOrMessage.preReleaseMessage
      : itemOrMessage;
  const trimmed = raw == null ? "" : String(raw).trim();
  return trimmed || DEFAULT_PRE_RELEASE_MESSAGE;
};

const TYPE_LABELS = {
  song: "Single",
  album: "Album",
  playlist: "Playlist",
  service: "Service",
};

const ABOUT_FIELD_KEYS = [
  "description",
  "notes",
  "credits",
  "about",
  "linerNotes",
  "story",
  "text",
];

const isTruthyFlag = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1" || v === "yes";
  }
  return Boolean(value);
};

export const getCatalogItemId = (item) => {
  if (!item || typeof item !== "object") return "";
  return String(item._id || item.identifier || item.slug || item.title || "");
};

export const isActiveCatalogItem = (item) => {
  if (!item || typeof item !== "object") return false;
  if (!Object.hasOwn(item, "active")) return true;
  return isTruthyFlag(item.active);
};

export const isListenableItem = (item) => {
  if (!item || typeof item !== "object") return false;
  if (!isActiveCatalogItem(item)) return false;
  const type = String(item.type || "")
    .trim()
    .toLowerCase();
  return LISTENABLE_TYPES.includes(type);
};

export const formatTypeLabel = (type) => {
  if (!type) return "";
  const key = String(type).trim().toLowerCase();
  if (TYPE_LABELS[key]) return TYPE_LABELS[key];
  return key.charAt(0).toUpperCase() + key.slice(1);
};

export const normalizeTags = (tags, { max = 0 } = {}) => {
  if (tags == null || tags === "") return [];
  let list = [];
  if (Array.isArray(tags)) {
    list = tags.flatMap((t) =>
      typeof t === "string" ? t.split(",") : [String(t)],
    );
  } else if (typeof tags === "string") {
    list = tags.split(",");
  } else {
    list = [String(tags)];
  }
  const cleaned = [
    ...new Set(
      list
        .map((t) => String(t).trim())
        .filter((t) => t && t.toLowerCase() !== "undefined"),
    ),
  ];
  if (max > 0) return cleaned.slice(0, max);
  return cleaned;
};

/** Prefer releaseDate; fall back to legacy typo field. */
export const getReleaseRaw = (item) => {
  if (!item) return "";
  const raw =
    item.releaseDate ||
    item.realeaseDate ||
    item.releasedAt ||
    item.release ||
    "";
  return raw == null ? "" : String(raw).trim();
};

export const getReleaseDate = (item) => {
  const raw = getReleaseRaw(item);
  if (!raw) return null;
  // Support ISO, yyyy-mm-dd, m-d-yyyy, timestamps
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  const mdy = raw.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (mdy) {
    const year = mdy[3].length === 2 ? `20${mdy[3]}` : mdy[3];
    const d = new Date(Number(year), Number(mdy[1]) - 1, Number(mdy[2]));
    if (!Number.isNaN(d.getTime())) return d;
  }
  const yearOnly = raw.match(/^(\d{4})$/);
  if (yearOnly) {
    const d = new Date(Number(yearOnly[1]), 0, 1);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

export const getReleaseYear = (item) => {
  const d = getReleaseDate(item);
  return d ? String(d.getFullYear()) : "";
};

const startOfLocalDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** True when releaseDate is after today (local calendar day). */
export const isUpcomingRelease = (item) => {
  const d = getReleaseDate(item);
  if (!d) return false;
  return startOfLocalDay(d) > startOfLocalDay(new Date());
};

/** Whole days until release day (0 = today, null = unknown / past). */
export const daysUntilRelease = (item) => {
  const d = getReleaseDate(item);
  if (!d) return null;
  const today = startOfLocalDay(new Date());
  const releaseDay = startOfLocalDay(d);
  const ms = releaseDay.getTime() - today.getTime();
  if (ms < 0) return null;
  return Math.round(ms / (24 * 60 * 60 * 1000));
};

/** Full calendar date for drop messaging, e.g. "March 15, 2026". */
export const formatReleaseFullDate = (item) => {
  const d = getReleaseDate(item);
  if (!d) return formatReleaseLabel(item);
  try {
    return d.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return formatReleaseLabel(item);
  }
};

/** Human countdown: "Drops today", "1 day until it drops", "12 days until it drops". */
export const formatReleaseCountdown = (item) => {
  const days = daysUntilRelease(item);
  if (days == null) return "";
  if (days === 0) return "Drops today";
  if (days === 1) return "Only 1 day until it drops";
  return `Only ${days} days until it drops`;
};

/** Storytelling date: "2024" or "Mar 2024" when month is meaningful. */
export const formatReleaseLabel = (item) => {
  const d = getReleaseDate(item);
  if (!d) {
    const raw = getReleaseRaw(item);
    const yearOnly = raw && raw.match(/(\d{4})/);
    return yearOnly ? yearOnly[1] : "";
  }
  const hasMonthPrecision = (() => {
    const raw = getReleaseRaw(item);
    if (!raw) return false;
    if (/^\d{4}$/.test(raw)) return false;
    return true;
  })();
  if (!hasMonthPrecision) return String(d.getFullYear());
  try {
    return d.toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(d.getFullYear());
  }
};

export const getArtistLine = (item) => {
  if (!item) return "";
  const artist = item.artist != null ? String(item.artist).trim() : "";
  return artist;
};

/**
 * Public subtitle under titles.
 * All releases are Ignite Elysium; artist is omitted from display.
 * Shows release label only (e.g. "Mar 2024" or "2024").
 */
export const getMetaSubtitle = (item) => {
  return formatReleaseLabel(item);
};

const isUsefulText = (value) => {
  if (value == null) return false;
  const text = String(value).trim();
  if (!text) return false;
  const lower = text.toLowerCase();
  return lower !== "undefined" && lower !== "false" && lower !== "null";
};

/** Long-form about/credits text if any known field is populated. */
export const getAboutText = (item) => {
  if (!item || typeof item !== "object") return "";
  for (const key of ABOUT_FIELD_KEYS) {
    if (!Object.hasOwn(item, key)) continue;
    if (isUsefulText(item[key])) return String(item[key]).trim();
  }
  return "";
};

/** All fan-facing prose fields that have content (for the About modal). */
export const getAboutSections = (item) => {
  if (!item || typeof item !== "object") return [];
  const labels = {
    description: "About",
    about: "About",
    notes: "Notes",
    credits: "Credits",
    linerNotes: "Liner notes",
    story: "Story",
    text: "Details",
  };
  const seen = new Set();
  const sections = [];
  for (const key of ABOUT_FIELD_KEYS) {
    if (!Object.hasOwn(item, key) || !isUsefulText(item[key])) continue;
    const text = String(item[key]).trim();
    // Skip duplicate body copy across synonym fields
    const fingerprint = text.slice(0, 120).toLowerCase();
    if (seen.has(fingerprint)) continue;
    seen.add(fingerprint);
    sections.push({
      key,
      label: labels[key] || key,
      text,
    });
  }
  return sections;
};

export const formatServiceLabel = (slug) => {
  if (!slug) return "";
  const known = {
    soundcloud: "SoundCloud",
    spotify: "Spotify",
    appleMusic: "Apple Music",
    applemusic: "Apple Music",
    youTube: "YouTube",
    youtube: "YouTube",
    bandcamp: "Bandcamp",
  };
  if (known[slug]) return known[slug];
  return String(slug)
    .replace(/([A-Z])/g, " $1")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

/**
 * Turn stored embed / player URLs into openable native listen URLs.
 * Falls back to the original URL when conversion is unclear.
 */
export const embedUrlToListenUrl = (rawUrl, serviceSlug = "") => {
  if (rawUrl == null) return "";
  const input = String(rawUrl).trim();
  if (!input) return "";

  let parsed;
  try {
    parsed = new URL(input);
  } catch {
    return "";
  }

  const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
  const path = parsed.pathname || "";
  const slug = String(serviceSlug || "").toLowerCase();

  // Spotify: embed/* → open.spotify.com/*
  if (host.includes("spotify.com") || slug === "spotify") {
    const embedMatch = path.match(
      /\/embed\/(track|album|playlist|episode|show)\/([a-zA-Z0-9]+)/,
    );
    if (embedMatch) {
      return `https://open.spotify.com/${embedMatch[1]}/${embedMatch[2]}`;
    }
    if (host.includes("open.spotify.com") && !path.includes("/embed/")) {
      return `https://open.spotify.com${path}`;
    }
  }

  // SoundCloud: player?url=… or api.soundcloud.com/…
  if (host.includes("soundcloud.com") || slug === "soundcloud") {
    const nested = parsed.searchParams.get("url");
    if (nested) {
      try {
        const decoded = decodeURIComponent(nested);
        const nestedUrl = new URL(decoded);
        const nestedHost = nestedUrl.hostname.replace(/^www\./, "");
        // Full public page already
        if (
          nestedHost === "soundcloud.com" &&
          !nestedUrl.pathname.startsWith("/player")
        ) {
          return `https://soundcloud.com${nestedUrl.pathname}${nestedUrl.search}`;
        }
        // api.soundcloud.com/tracks|playlists|users/ID — API path often works as redirect
        if (nestedHost === "api.soundcloud.com") {
          return decoded;
        }
        return decoded;
      } catch {
        try {
          return decodeURIComponent(nested);
        } catch {
          return nested;
        }
      }
    }
    if (host === "soundcloud.com" && !path.startsWith("/player")) {
      return `https://soundcloud.com${path}${parsed.search}`;
    }
    if (host === "api.soundcloud.com") {
      return input;
    }
  }

  // Apple Music: embed.music.apple.com → music.apple.com
  if (
    host.includes("music.apple.com") ||
    slug === "applemusic" ||
    slug === "apple music"
  ) {
    if (host.startsWith("embed.music.apple.com")) {
      return `https://music.apple.com${path}${parsed.search}`;
    }
    if (host === "music.apple.com") {
      return `https://music.apple.com${path}${parsed.search}`;
    }
  }

  // YouTube embed → watch URL
  if (
    host.includes("youtube.com") ||
    host === "youtu.be" ||
    slug === "youtube"
  ) {
    if (path.startsWith("/embed/")) {
      const id = path.replace(/^\/embed\//, "").split("/")[0];
      if (id) return `https://www.youtube.com/watch?v=${id}`;
    }
    if (host === "youtu.be") {
      const id = path.replace(/^\//, "").split("/")[0];
      if (id) return `https://www.youtube.com/watch?v=${id}`;
    }
    if (path === "/watch" && parsed.searchParams.get("v")) {
      return `https://www.youtube.com/watch?v=${parsed.searchParams.get("v")}`;
    }
  }

  // Bandcamp: track/album page if not already an embed-only widget
  if (host.includes("bandcamp.com") || slug === "bandcamp") {
    if (path.includes("/EmbeddedPlayer")) {
      const track = parsed.searchParams.get("track");
      const album = parsed.searchParams.get("album");
      // Can't always rebuild a public URL from numeric IDs alone
      if (track || album) return input;
    }
    return `https://${parsed.hostname}${path}${parsed.search}`;
  }

  // Already a normal http(s) link — use as-is
  if (parsed.protocol === "http:" || parsed.protocol === "https:") {
    return input;
  }

  return "";
};

/**
 * Fan-facing listen links: { slug, label, href } for each service with a URL.
 * href is converted from embed → native page when possible.
 */
export const getListeningServices = (item) => {
  const obj = item?.sourceURLObj;
  if (!obj || typeof obj !== "object") return [];

  return Object.entries(obj)
    .map(([name, url]) => {
      const raw = url != null ? String(url).trim() : "";
      if (!raw) return null;
      const href = embedUrlToListenUrl(raw, name);
      if (!href) return null;
      return {
        slug: name,
        label: formatServiceLabel(name),
        href,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Pull distinct values from aggregated catalogMetadata.
 * Shape is usually [count, ...values] or tags: [count, arrayOfTags].
 */
export const getMetadataValues = (catalogMetadata, key) => {
  if (!catalogMetadata || !key) return [];
  const raw = catalogMetadata[key];
  if (raw == null) return [];
  if (!Array.isArray(raw)) {
    if (typeof raw === "string") return normalizeTags(raw);
    return [];
  }
  const rest = raw.slice(1);
  const chips = [];
  rest.forEach((entry) => {
    if (Array.isArray(entry)) chips.push(...entry);
    else if (entry != null && entry !== "") chips.push(entry);
  });
  return [
    ...new Set(
      chips
        .map((c) => String(c).trim())
        .filter((c) => c && c.toLowerCase() !== "undefined"),
    ),
  ];
};

export const getListenableCatalogItems = (catalog) => {
  if (!catalog || typeof catalog !== "object") return [];
  return Object.values(catalog).filter(isListenableItem);
};

export const sortByReleaseNewest = (items) => {
  return [...items].sort((a, b) => {
    const da = getReleaseDate(a);
    const db = getReleaseDate(b);
    const ta = da ? da.getTime() : 0;
    const tb = db ? db.getTime() : 0;
    if (tb !== ta) return tb - ta;
    const titleA = String(a?.title || "").toLowerCase();
    const titleB = String(b?.title || "").toLowerCase();
    return titleA.localeCompare(titleB);
  });
};

const titleOf = (item) => String(item?.title || "").toLowerCase();
const typeOf = (item) => String(item?.type || "").toLowerCase();
const releaseTime = (item) => {
  const d = getReleaseDate(item);
  return d ? d.getTime() : 0;
};

/** Public catalog browse sorts. Default: newest first. */
export const sortCatalogItems = (items, sortMode = "newest") => {
  const list = [...(items || [])];
  switch (sortMode) {
    case "oldest":
      return list.sort((a, b) => {
        const diff = releaseTime(a) - releaseTime(b);
        if (diff !== 0) return diff;
        return titleOf(a).localeCompare(titleOf(b));
      });
    case "title-asc":
      return list.sort((a, b) => titleOf(a).localeCompare(titleOf(b)));
    case "title-desc":
      return list.sort((a, b) => titleOf(b).localeCompare(titleOf(a)));
    case "type":
      return list.sort((a, b) => {
        const td = typeOf(a).localeCompare(typeOf(b));
        if (td !== 0) return td;
        return titleOf(a).localeCompare(titleOf(b));
      });
    case "newest":
    default:
      return sortByReleaseNewest(list);
  }
};

export const filterCatalogForBrowse = (
  items,
  { search = "", type = "", tags = [] } = {},
) => {
  const q = search.trim().toLowerCase();
  const typeFilter = type.trim().toLowerCase();
  const tagFilters = (Array.isArray(tags) ? tags : [])
    .map((t) => String(t).trim().toLowerCase())
    .filter(Boolean);

  return items.filter((item) => {
    if (!isListenableItem(item)) return false;
    if (
      typeFilter &&
      String(item.type || "")
        .trim()
        .toLowerCase() !== typeFilter
    ) {
      return false;
    }
    // OR match: item must include at least one selected vibe tag
    if (tagFilters.length > 0) {
      const itemTags = normalizeTags(item.tags).map((t) => t.toLowerCase());
      if (!tagFilters.some((t) => itemTags.includes(t))) return false;
    }
    if (!q) return true;
    const haystack = [
      item.title,
      item.type,
      item.slug,
      ...normalizeTags(item.tags),
      formatReleaseLabel(item),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
};

/**
 * Related listening: shared tags (strong), same type, same year.
 * Excludes the seed item and inactive/non-listenable entries.
 */
export const getRelatedItems = (seed, allItems, { limit = 3 } = {}) => {
  if (!seed || !Array.isArray(allItems) || allItems.length === 0) return [];
  const seedId = getCatalogItemId(seed);
  const seedTags = new Set(
    normalizeTags(seed.tags).map((t) => t.toLowerCase()),
  );
  const seedType = String(seed.type || "")
    .trim()
    .toLowerCase();
  const seedYear = getReleaseYear(seed);

  const scored = allItems
    .filter((item) => {
      if (!isListenableItem(item)) return false;
      const id = getCatalogItemId(item);
      return id && id !== seedId;
    })
    .map((item) => {
      let score = 0;
      const itemTags = normalizeTags(item.tags).map((t) => t.toLowerCase());
      const sharedTags = itemTags.filter((t) => seedTags.has(t));
      score += sharedTags.length * 3;
      if (
        seedType &&
        String(item.type || "")
          .trim()
          .toLowerCase() === seedType
      ) {
        score += 2;
      }
      if (seedYear && getReleaseYear(item) === seedYear) {
        score += 1;
      }
      return { item, score, sharedTags };
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return String(a.item.title || "").localeCompare(
        String(b.item.title || ""),
      );
    });

  return scored.slice(0, limit).map((row) => row.item);
};

export const catalogItemAnchorId = (item) => {
  const id = getCatalogItemId(item);
  if (!id) return "";
  return `catalog-item-${id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
};

export const scrollToCatalogItem = (item) => {
  const anchor = catalogItemAnchorId(item);
  if (!anchor) return;
  const el = document.getElementById(anchor);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("catalog-item-highlight");
    window.setTimeout(() => {
      el.classList.remove("catalog-item-highlight");
    }, 1600);
  }
};
